import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, PencilLine, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/app/common";
import { useMe } from "@/components/app/MeContext";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { PendingPlanChanges } from "@/components/planning/PlanChange";

export function DecisionButtons({ approval, onDone }) {
  const { can, me } = useMe();
  const nav = useNavigate();
  const [mode, setMode] = useState(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  if (approval.status !== "pending" || !can("approve")) return null;
  const own = approval.requested_by?.user_id === me.user_id && !(me.roles || []).includes("admin");
  if (own) return <span className="text-xs text-slate-500" data-testid="own-request-note">Awaiting another approver</span>;
  const submit = async () => {
    setBusy(true);
    try {
      if (mode === "revise") {
        await api.post(`/approvals/${approval.id}/revise`, { reason: comment });
        toast.success("LM moved to draft for your changes - approve it from the LM page when done");
        nav(`/orders/${approval.entity_id}`);
        return;
      }
      await api.post(`/approvals/${approval.id}/decision`, { decision: mode, comment });
      toast.success(`Order ${mode}`);
      setMode(null);
      setComment("");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <div className="flex gap-2">
        <Button size="sm" onClick={() => setMode("approved")} data-testid={`approve-btn-${approval.id}`}><Check className="h-4 w-4 mr-1" />Approve</Button>
        <Button size="sm" variant="outline" onClick={() => setMode("rejected")} data-testid={`reject-btn-${approval.id}`}><X className="h-4 w-4 mr-1" />Reject</Button>
        {can("orders.edit") && <Button size="sm" variant="outline" onClick={() => setMode("revise")} data-testid={`revise-approve-btn-${approval.id}`}><PencilLine className="h-4 w-4 mr-1" />Revise & approve</Button>}
      </div>
      <Dialog open={!!mode} onOpenChange={(v) => !v && setMode(null)}>
        <DialogContent data-testid="decision-dialog">
          <DialogHeader><DialogTitle>{{ approved: "Approve", rejected: "Reject", revise: "Revise & approve" }[mode]} {approval.entity_label}</DialogTitle></DialogHeader>
          {mode === "revise" && <p className="text-xs text-slate-600">The LM goes back to draft as a new revision (the current version is kept in history). Make your changes on the LM page, then press “Approve revised LM” there with your comment.</p>}
          <Textarea rows={3} placeholder={mode === "approved" ? "Comment (optional)" : mode === "revise" ? "What needs changing? (required)" : "Reason (required)"} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="decision-comment-input" />
          <DialogFooter><Button onClick={submit} disabled={busy || (mode === "revise" && comment.trim().length < 3)} data-testid="decision-confirm-btn">{mode === "revise" ? "Start revision" : "Confirm"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function ApproverRevisionBanner({ order, reload }) {
  const { me } = useMe();
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState("");
  const r = order.approver_revision;
  if (!r || order.status !== "draft") return null;
  const mine = r.by?.user_id === me.user_id;
  const go = async () => {
    try { await api.post(`/orders/${order.id}/approve-revision`, { decision: "approved", comment }); toast.success("Revised LM approved"); setOpen(false); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2 rounded border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-900" data-testid="approver-revision-banner">
      <span>Being revised by approver <b>{r.by?.name}</b> (R{r.revision}): {r.reason}. Requested by {r.requested_by?.name}.</span>
      {mine && <Button size="sm" className="ml-auto" onClick={() => setOpen(true)} data-testid="approve-revision-btn"><Check className="h-4 w-4 mr-1" />Approve revised LM</Button>}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="approve-revision-dialog">
          <DialogHeader><DialogTitle>Approve revised {order.order_no}</DialogTitle></DialogHeader>
          <Textarea rows={3} placeholder="Comment on your changes (optional)" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="approve-revision-comment" />
          <DialogFooter><Button onClick={go} data-testid="approve-revision-confirm">Approve</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function ApprovalsTab({ order, reload }) {
  return (
    <div className="space-y-3" data-testid="approvals-tab">
      <PendingPlanChanges lmId={order.id} compact />
      {order.approvals.map((a) => (
        <div key={a.id} className="border border-slate-200 rounded p-3 flex flex-col md:flex-row md:items-center justify-between gap-3" data-testid={`approval-row-${a.id}`}>
          <div>
            <div className="flex items-center gap-2"><span className="text-sm font-medium">{a.step}</span><StatusBadge status={a.status} /></div>
            <div className="text-xs text-slate-500 mt-1">Requested by {a.requested_by?.name} · {fmtDateTime(a.requested_at)}</div>
            {a.decided_by && <div className="text-xs text-slate-500">Decided by {a.decided_by.name} · {fmtDateTime(a.decided_at)}{a.comment ? ` — “${a.comment}”` : ""}</div>}
            {a.revised_by_approver && <div className="text-xs text-violet-700" data-testid={`approval-revised-note-${a.id}`}>Revised by the approver (R{a.revision}): {a.revision_reason}</div>}
          </div>
          <DecisionButtons approval={a} onDone={reload} />
        </div>
      ))}
      {order.approvals.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">Not submitted for approval yet</div>}
    </div>
  );
}
